import { normalizePackageTypeIcon, type PackageTypeIconKey } from '@pcrm/shared/icons';
import {
  Activity,
  Antenna,
  BellRing,
  Boxes,
  Briefcase,
  Building2,
  Cable,
  Cctv,
  Cloud,
  Cog,
  Container,
  Cpu,
  Database,
  Fingerprint,
  Gauge,
  Globe,
  HardDrive,
  Headset,
  KeyRound,
  LifeBuoy,
  Lock,
  type LucideIcon,
  Mail,
  MapPin,
  MessageSquare,
  MonitorPlay,
  Network,
  Package,
  Phone,
  PhoneCall,
  PlugZap,
  Printer,
  Radio,
  RadioTower,
  Router,
  Rss,
  SatelliteDish,
  Server,
  ShieldCheck,
  Signal,
  Smartphone,
  Tv,
  Users,
  Video,
  Warehouse,
  Waypoints,
  Wifi,
  Wrench,
  Zap,
} from 'lucide-react';

/**
 * Numele din `@pcrm/shared/icons` → componenta `lucide-react`.
 *
 * Maparea stă **aici**, nu în `shared`: `shared` e importat și de backend, care n-are ce
 * face cu componente React. Acolo e lista de nume, aici sunt componentele — aceeași
 * împărțire ca la iconurile firmelor și ca la permisiuni.
 *
 * Tabelul e `Record<PackageTypeIconKey, …>`, deci **un nume adăugat în listă și uitat aici
 * nu compilează**. Asta e toată garanția de care are nevoie randarea.
 *
 * Stă în `features/settings` pentru că de acolo se alege iconul, dar se citește din toată
 * aplicația — la fel ca `packages.api.ts`, care e tot aici și e folosit de abonamente.
 */
const ICONS: Record<PackageTypeIconKey, LucideIcon> = {
  Package,
  Wifi,
  Router,
  Network,
  Cable,
  Waypoints,
  Antenna,
  RadioTower,
  SatelliteDish,
  Signal,
  Globe,
  Radio,
  Rss,
  Phone,
  PhoneCall,
  Smartphone,
  Headset,
  MessageSquare,
  Mail,
  Server,
  Cpu,
  HardDrive,
  Database,
  Cloud,
  Container,
  Boxes,
  Building2,
  Warehouse,
  Tv,
  MonitorPlay,
  Video,
  Cctv,
  ShieldCheck,
  Lock,
  KeyRound,
  Fingerprint,
  Wrench,
  Cog,
  LifeBuoy,
  Users,
  Briefcase,
  Activity,
  Gauge,
  BellRing,
  Zap,
  PlugZap,
  MapPin,
  Printer,
};

/**
 * Etichete în română, pentru `aria-label` și pentru tooltip-ul de sub cursor.
 *
 * Nu sunt numele iconului din `lucide-react`, ci **ce reprezintă la noi**: „Fibră /
 * cablu", nu „Cable". Operatorul caută serviciul, nu simbolul.
 */
const LABELS: Record<PackageTypeIconKey, string> = {
  Package: 'Serviciu (neutru)',
  Wifi: 'Wi-Fi',
  Router: 'Router',
  Network: 'Rețea',
  Cable: 'Fibră / cablu',
  Waypoints: 'Interconectare',
  Antenna: 'Antenă',
  RadioTower: 'Turn radio',
  SatelliteDish: 'Antenă satelit',
  Signal: 'Semnal',
  Globe: 'Internet',
  Radio: 'Radio',
  Rss: 'Flux',
  Phone: 'Telefonie',
  PhoneCall: 'Apeluri',
  Smartphone: 'Mobil',
  Headset: 'Suport / call center',
  MessageSquare: 'SMS / mesagerie',
  Mail: 'E-mail',
  Server: 'Server',
  Cpu: 'Server dedicat',
  HardDrive: 'Stocare',
  Database: 'Bază de date',
  Cloud: 'Cloud',
  Container: 'Container / VPS',
  Boxes: 'Resurse grupate',
  Building2: 'Colocare',
  Warehouse: 'Datacenter',
  Tv: 'Televiziune',
  MonitorPlay: 'Streaming',
  Video: 'Video',
  Cctv: 'Supraveghere video',
  ShieldCheck: 'Securitate',
  Lock: 'VPN / acces securizat',
  KeyRound: 'Licențe / chei',
  Fingerprint: 'Identitate',
  Wrench: 'Mentenanță',
  Cog: 'Servicii administrate',
  LifeBuoy: 'Asistență',
  Users: 'Echipă dedicată',
  Briefcase: 'Servicii de business',
  Activity: 'Monitorizare',
  Gauge: 'Bandă / viteză',
  BellRing: 'Alertare',
  Zap: 'Energie',
  PlugZap: 'Alimentare',
  MapPin: 'Locație',
  Printer: 'Tipărire',
};

/**
 * Grupurile din grila de alegere, în ordinea în care apar.
 *
 * Există pentru că patruzeci și opt de simboluri într-o singură pătură nu se parcurg cu
 * ochiul: cine caută „ceva de telefonie" trebuie să aibă unde se uita. Împărțirea e
 * **aceeași** cu comentariile de familie din `@pcrm/shared/icons` — acolo e ordinea, aici
 * sunt titlurile, pentru că `shared` ajunge și în backend.
 *
 * ⚠️ Un nume adăugat în `shared` și uitat aici **compilează**, dar nu apare în grilă — nu
 * există `Record` care să-l prindă. De asta `package-type-icons.spec.ts` verifică faptul că
 * grupurile acoperă lista întreagă, fiecare cheie exact o dată.
 */
export interface PackageTypeIconGroup {
  readonly label: string;
  readonly keys: readonly PackageTypeIconKey[];
}

export const PACKAGE_TYPE_ICON_GROUPS: readonly PackageTypeIconGroup[] = [
  { label: 'Neutru', keys: ['Package'] },
  {
    label: 'Internet și rețea',
    keys: [
      'Wifi',
      'Router',
      'Network',
      'Cable',
      'Waypoints',
      'Antenna',
      'RadioTower',
      'SatelliteDish',
      'Signal',
      'Globe',
      'Radio',
      'Rss',
    ],
  },
  {
    label: 'Telefonie și comunicații',
    keys: ['Phone', 'PhoneCall', 'Smartphone', 'Headset', 'MessageSquare', 'Mail'],
  },
  {
    label: 'Datacenter și găzduire',
    keys: [
      'Server',
      'Cpu',
      'HardDrive',
      'Database',
      'Cloud',
      'Container',
      'Boxes',
      'Building2',
      'Warehouse',
    ],
  },
  { label: 'TV și video', keys: ['Tv', 'MonitorPlay', 'Video', 'Cctv'] },
  { label: 'Securitate', keys: ['ShieldCheck', 'Lock', 'KeyRound', 'Fingerprint'] },
  {
    label: 'Servicii administrate',
    keys: ['Wrench', 'Cog', 'LifeBuoy', 'Users', 'Briefcase'],
  },
  { label: 'Monitorizare', keys: ['Activity', 'Gauge', 'BellRing'] },
  { label: 'Altele', keys: ['Zap', 'PlugZap', 'MapPin', 'Printer'] },
];

export function packageTypeIcon(key: string | null | undefined): LucideIcon {
  return ICONS[normalizePackageTypeIcon(key)];
}

export function packageTypeIconLabel(key: PackageTypeIconKey): string {
  return LABELS[key];
}

/**
 * Iconul unui tip de pachet, la dimensiunea din liste și din tabele.
 *
 * `aria-hidden`: simbolul **dublează** denumirea tipului, care e scrisă alături peste tot
 * unde apare. Anunțat de cititorul de ecran, ar citi aceeași informație de două ori.
 */
export function PackageTypeIcon({ icon, className }: { icon: string | null; className?: string }) {
  const Icon = packageTypeIcon(icon);
  return <Icon aria-hidden className={className ?? 'size-4 shrink-0'} />;
}
